import { useCallback, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchServiceProviderManifests, fetchConnectorManifest } from '@/services/logicAppsApi';
import type { WorkflowDefinition, Action } from '@/types/workflowDefinition';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import type { NodeConnectorInfo, ConnectorManifest } from '@/types/connections';
import { useBuiltInOperationsQuery } from '@/hooks/useAllOperations';
import { getBuiltInManifest } from '@/data/manifests';
import type { DiscoveryOperation } from '@/services/operationsService';

/**
 * Connector reference extracted from an action or trigger
 */
type ConnectorRef = {
  connectionName: string;
  serviceProviderId?: string; // e.g., "/serviceProviders/AzureBlob"
  apiId?: string; // e.g., "/providers/Microsoft.Web/locations/.../managedApis/office365"
  agentModelType?: string; // e.g., "AzureOpenAI"
  agentModelName?: string; // e.g., "gpt-4.1"
};

/**
 * Extract connection name from action/trigger inputs
 */
const extractConnectionName = (inputs: Record<string, unknown> | undefined): string | undefined => {
  if (!inputs) return undefined;

  // Check for service provider connection
  const serviceProviderConfig = inputs.serviceProviderConfiguration as Record<string, unknown> | undefined;
  if (serviceProviderConfig?.connectionName) {
    return serviceProviderConfig.connectionName as string;
  }

  // Check for managed API connection
  const host = inputs.host as Record<string, unknown> | undefined;
  const connection = host?.connection as Record<string, unknown> | undefined;
  if (connection?.referenceName) {
    return connection.referenceName as string;
  }

  // Check for agent connection (modelConfigurations.model1.referenceName)
  const modelConfigurations = inputs.modelConfigurations as Record<string, unknown> | undefined;
  const model1 = modelConfigurations?.model1 as Record<string, unknown> | undefined;
  if (model1?.referenceName) {
    return model1.referenceName as string;
  }

  return undefined;
};

/**
 * Extract service provider ID from action/trigger inputs
 */
const extractServiceProviderId = (inputs: Record<string, unknown> | undefined): string | undefined => {
  if (!inputs) return undefined;

  const serviceProviderConfig = inputs.serviceProviderConfiguration as Record<string, unknown> | undefined;
  if (serviceProviderConfig?.serviceProviderId) {
    return serviceProviderConfig.serviceProviderId as string;
  }

  return undefined;
};

/**
 * Extract managed-API connector ID from action/trigger inputs.
 * For ApiConnection / ApiConnectionWebhook / ApiConnectionNotification, the host
 * carries an apiId like "/subscriptions/.../managedApis/office365" or the short
 * form "/managedApis/office365".
 */
const extractApiId = (inputs: Record<string, unknown> | undefined): string | undefined => {
  if (!inputs) return undefined;
  const host = inputs.host as Record<string, unknown> | undefined;
  const apiId = host?.apiId;
  return typeof apiId === 'string' && apiId.length > 0 ? apiId : undefined;
};

/**
 * Display ref keyed by action name, regardless of whether a connection exists yet.
 * Used so freshly-added actions and loaded actions both resolve their connector
 * icon / name from the manifest cache instead of carrying display fields in the
 * persisted workflow JSON.
 */
type OperationDisplayRef = {
  /** Action or trigger name within the workflow definition. */
  actionName: string;
  /** "/serviceProviders/<name>" when this is a Service Provider operation. */
  serviceProviderId?: string;
  /** Managed-API id (e.g. "/managedApis/office365"). */
  apiId?: string;
  /** Workflow JSON `type` (e.g. "Batch", "ChunkText", "ApiConnection"). */
  type?: string;
  /** Optional `kind` discriminator (e.g. "liquid"). */
  kind?: string;
};

type OperationKind = 'trigger' | 'action';
type OperationVisitor = (
  name: string,
  op: Record<string, unknown>,
  kind: OperationKind,
) => void;

/**
 * Walk every trigger and action (including nested else/cases/default and Agent
 * tool inner actions) in a workflow definition and invoke the visitor for each.
 * Single source of truth for tree traversal — extractors layer their own
 * per-op logic on top.
 */
const visitOperations = (
  workflow: WorkflowDefinition | null,
  visit: OperationVisitor,
): void => {
  if (!workflow?.definition) return;

  for (const [name, trigger] of Object.entries(workflow.definition.triggers ?? {})) {
    visit(name, trigger as Record<string, unknown>, 'trigger');
  }

  const visitBranch = (block: unknown): void => {
    if (block && typeof block === 'object') {
      walk((block as Record<string, unknown>).actions as Record<string, Action> | undefined);
    }
  };

  const visitGroup = (group: unknown): void => {
    if (group && typeof group === 'object') {
      for (const child of Object.values(group as Record<string, unknown>)) {
        visitBranch(child);
      }
    }
  };

  const walk = (actions: Record<string, Action> | undefined): void => {
    if (!actions) return;
    for (const [name, action] of Object.entries(actions)) {
      const a = action as Record<string, unknown>;
      visit(name, a, 'action');
      if (a.actions) walk(a.actions as Record<string, Action>);
      visitBranch(a.else);
      visitBranch(a.default);
      visitGroup(a.cases);
      visitGroup(a.tools);
    }
  };
  walk(workflow.definition.actions);
};

/**
 * Per-action display refs — one entry per action/trigger that maps to a
 * connector manifest (Service Provider id, managed-API id, or built-in type).
 *
 * Exported for tests; do not call from app code (use the hook instead).
 */
export const extractOperationDisplayRefs = (
  workflow: WorkflowDefinition | null,
): OperationDisplayRef[] => {
  const refs: OperationDisplayRef[] = [];
  visitOperations(workflow, (actionName, op) => {
    const inputs = op.inputs as Record<string, unknown> | undefined;
    const serviceProviderId = extractServiceProviderId(inputs);
    const apiId = extractApiId(inputs);
    const type = typeof op.type === 'string' ? op.type : undefined;
    const kind = typeof op.kind === 'string' ? op.kind : undefined;
    if (!serviceProviderId && !apiId && !type) return;
    const ref: OperationDisplayRef = { actionName };
    if (serviceProviderId) ref.serviceProviderId = serviceProviderId;
    if (apiId) ref.apiId = apiId;
    if (type) ref.type = type;
    if (kind) ref.kind = kind;
    refs.push(ref);
  });
  return refs;
};

/**
 * Extract agent model info from action inputs
 * Agent actions have: parameters.agentModelType (e.g., "AzureOpenAI")
 * and parameters.agentModelSettings.deploymentModelProperties.name (e.g., "gpt-4.1")
 */
const extractAgentModelInfo = (
  inputs: Record<string, unknown> | undefined
): { agentModelType?: string; agentModelName?: string } => {
  if (!inputs) return {};

  // Check root-level agentModelType first (FoundryHostedAgent), then nested parameters
  const parameters = inputs.parameters as Record<string, unknown> | undefined;
  const agentModelType = (inputs.agentModelType as string | undefined) ?? (parameters?.agentModelType as string | undefined);
  const agentModelSettings = parameters?.agentModelSettings as Record<string, unknown> | undefined;
  const deploymentModelProperties = agentModelSettings?.deploymentModelProperties as
    | Record<string, unknown>
    | undefined;
  const agentModelName = deploymentModelProperties?.name as string | undefined;

  const result: { agentModelType?: string; agentModelName?: string } = {};
  if (agentModelType) {
    result.agentModelType = agentModelType;
  }
  if (agentModelName) {
    result.agentModelName = agentModelName;
  }
  return result;
};

/**
 * Extract all connector references from a workflow definition
 */
const extractConnectorRefs = (workflow: WorkflowDefinition | null): ConnectorRef[] => {
  const refs: ConnectorRef[] = [];
  visitOperations(workflow, (_name, op, kind) => {
    const inputs = op.inputs as Record<string, unknown> | undefined;
    const connectionName = extractConnectionName(inputs);
    if (!connectionName) return;

    const ref: ConnectorRef = { connectionName };
    const serviceProviderId = extractServiceProviderId(inputs);
    if (serviceProviderId) ref.serviceProviderId = serviceProviderId;
    if (kind === 'action') {
      const { agentModelType, agentModelName } = extractAgentModelInfo(inputs);
      if (agentModelType) ref.agentModelType = agentModelType;
      if (agentModelName) ref.agentModelName = agentModelName;
    }
    refs.push(ref);
  });
  return refs;
};

const workflowConnectorSignatureCache = new WeakMap<WorkflowDefinition, string>();

export const getWorkflowConnectorSignature = (
  workflow: WorkflowDefinition | null,
): string => {
  if (!workflow) return '[]';
  const cached = workflowConnectorSignatureCache.get(workflow);
  if (cached !== undefined) return cached;

  const signature = JSON.stringify([
    extractConnectorRefs(workflow),
    extractOperationDisplayRefs(workflow),
  ]);
  workflowConnectorSignatureCache.set(workflow, signature);
  return signature;
};

/**
 * Enrich connector refs with info from connections.json
 */
const enrichWithConnectionInfo = (
  refs: ConnectorRef[],
  connections: ConnectionsConfiguration | null
): ConnectorRef[] => {
  if (!connections) return refs;

  return refs.map((ref) => {
    // Check service provider connections
    const serviceProviderConn = connections.serviceProviderConnections?.[ref.connectionName];
    if (serviceProviderConn) {
      return {
        ...ref,
        serviceProviderId: ref.serviceProviderId ?? serviceProviderConn.serviceProvider.id,
      };
    }

    // Check managed API connections
    const managedApiConn = connections.managedApiConnections?.[ref.connectionName];
    if (managedApiConn) {
      return {
        ...ref,
        apiId: managedApiConn.api.id,
      };
    }

    return ref;
  });
};

/**
 * Extract unique service provider IDs from connector refs
 */
const getUniqueServiceProviderIds = (refs: ConnectorRef[]): string[] => {
  const ids = new Set<string>();
  for (const ref of refs) {
    if (ref.serviceProviderId) {
      ids.add(ref.serviceProviderId);
    }
  }
  return Array.from(ids);
};

/**
 * Extract API name from a managed API ID
 * e.g., "/subscriptions/.../providers/Microsoft.Web/locations/.../managedApis/office365" -> "office365"
 */
const extractApiName = (apiId: string): string | undefined => {
  const match = apiId.match(/managedApis\/([^/]+)$/);
  return match?.[1];
};

/**
 * Build the connector-map key for a per-action display fallback. Action names
 * are user-editable, so they're namespaced to avoid colliding with managed-API
 * names (`office365`) or connection reference keys when an author renames an
 * action to a short string. Producers and consumers MUST go through this
 * helper so the prefix can change in one place.
 */
export const actionNameKey = (actionName: string): string => `action:${actionName}`;

/** Discovery brandColor can arrive as a number (e.g. 0x0078D4) or string. */
const normalizeBuiltInBrandColor = (color: unknown): string | undefined => {
  if (typeof color === 'string' && color.length > 0) return color;
  if (typeof color === 'number') return `#${color.toString(16).padStart(6, '0')}`;
  return undefined;
};

/** Build a NodeConnectorInfo from a fetched connector manifest. */
const manifestToInfo = (
  manifest: ConnectorManifest,
  connectorType: NodeConnectorInfo['connectorType'],
): NodeConnectorInfo => ({
  displayName: manifest.displayName,
  iconUri: manifest.iconUri,
  brandColor: manifest.brandColor,
  connectorType,
});

/** Build a NodeConnectorInfo from a built-in runtime operation entry. */
const builtInOpToInfo = (op: DiscoveryOperation, fallbackName: string): NodeConnectorInfo => {
  const iconUri = op.properties?.iconUri ?? op.properties?.api?.iconUri;
  const brandColor = normalizeBuiltInBrandColor(
    op.properties?.brandColor ?? op.properties?.api?.brandColor,
  );
  const info: NodeConnectorInfo = {
    displayName: op.properties?.api?.displayName ?? op.properties?.summary ?? fallbackName,
    connectorType: 'builtIn',
  };
  if (iconUri) info.iconUri = iconUri;
  if (brandColor) info.brandColor = brandColor;
  return info;
};

type UseWorkflowConnectorsResult = {
  connectorMap: ReadonlyMap<string, NodeConnectorInfo>;
  getConnectorInfo: (connectionName: string) => NodeConnectorInfo | undefined;
  isLoading: boolean;
};

type WorkflowStoreState = ReturnType<typeof useWorkflowStore.getState>;
type ConnectionStoreState = ReturnType<typeof useConnectionStore.getState>;
type AppStoreState = ReturnType<typeof useAppStore.getState>;

const selectWorkflowConnectorSignature = (state: WorkflowStoreState) =>
  getWorkflowConnectorSignature(state.workflow);
const selectConnections = (state: WorkflowStoreState) => state.connections;
const selectConnectionReferences = (state: ConnectionStoreState) => state.references;
const selectSelectedAppName = (state: AppStoreState) => state.selectedApp?.name ?? null;
const selectSelectedProject = (state: AppStoreState) => state.selectedProject;

/**
 * Hook that provides connector info (displayName, icon, brandColor) for workflow nodes
 */
export const useWorkflowConnectors = (): UseWorkflowConnectorsResult => {
  const workflowConnectorSignature = useWorkflowStore(selectWorkflowConnectorSignature);
  const workflowConnectorSnapshot = useMemo(
    () => ({
      signature: workflowConnectorSignature,
      workflow: useWorkflowStore.getState().workflow,
    }),
    [workflowConnectorSignature],
  );
  const workflow = workflowConnectorSnapshot.workflow;
  const connections = useWorkflowStore(selectConnections);
  const connectionReferences = useConnectionStore(selectConnectionReferences);
  const { getOttoToken } = useAuth();
  const selectedAppName = useAppStore(selectSelectedAppName);
  const selectedProject = useAppStore(selectSelectedProject);
  const { data: builtInOperations } = useBuiltInOperationsQuery();

  // Built-in runtime ops indexed by lowercased workflow `type` so literal-type
  // ops (Batch, ChunkText, XmlCompose, …) with no SP id / managed-API id can
  // still resolve an icon. Entries without an iconUri are dropped — there's
  // nothing to render and the static-manifest fallback handles displayName.
  const builtInOpsByType = useMemo(() => {
    const map = new Map<string, DiscoveryOperation>();
    for (const op of builtInOperations ?? []) {
      const type = op.properties?.operationType?.toLowerCase();
      if (!type || map.has(type)) continue;
      if (!op.properties?.iconUri && !op.properties?.api?.iconUri) continue;
      map.set(type, op);
    }
    return map;
  }, [builtInOperations]);

  // Extract connector refs from workflow
  const connectorRefs = useMemo(() => {
    return extractConnectorRefs(workflow);
  }, [workflow]);

  // Per-action display refs — populated for every action/trigger that maps to a
  // connector manifest, even when no connection has been picked yet. Used to
  // resolve icon / display name from the manifest at render time so the
  // workflow JSON doesn't have to carry display fields.
  const operationDisplayRefs = useMemo(() => {
    return extractOperationDisplayRefs(workflow);
  }, [workflow]);

  // Enrich with connection info
  const enrichedRefs = useMemo(
    () => enrichWithConnectionInfo(connectorRefs, connections),
    [connectorRefs, connections]
  );

  // Determine if we have service provider connections to fetch.
  // Combine ids surfaced by connection-bound refs with ids extracted directly
  // from action inputs — the latter covers actions that haven't picked a
  // connection yet (e.g. a freshly-dropped Document Intelligence action).
  const serviceProviderIds = useMemo(() => {
    const ids = new Set<string>(getUniqueServiceProviderIds(enrichedRefs));
    for (const op of operationDisplayRefs) {
      if (op.serviceProviderId) ids.add(op.serviceProviderId);
    }
    return Array.from(ids);
  }, [enrichedRefs, operationDisplayRefs]);

  // Determine if we have managed API connections to fetch
  // Include API names from both workflow definition refs AND connection store references
  // so that newly-created connections (not yet used in any action) also have icons
  const managedApiNames = useMemo(() => {
    const names = new Set<string>();

    // Extract from enriched refs (workflow definition + connections.json)
    for (const ref of enrichedRefs) {
      if (ref.apiId) {
        const apiName = extractApiName(ref.apiId);
        if (apiName) {
          names.add(apiName);
        }
      } else if (!ref.serviceProviderId && !ref.agentModelType) {
        // No apiId from connections.json — check connection store for the correct API ID
        // before falling back to using the connection name (which may include a unique suffix)
        const storeRef = connectionReferences[ref.connectionName];
        const storeApiId = storeRef?.api?.id ?? '';
        const storeMatch = storeApiId.match(/managedApis\/([^/]+)$/);
        if (storeMatch?.[1]) {
          names.add(storeMatch[1]);
        }
      }
    }

    // Also extract managed API names from connection store references
    for (const ref of Object.values(connectionReferences)) {
      const apiId = ref.api?.id ?? '';

      // Canonical format: "/managedApis/office365"
      if (apiId.includes('/managedApis/')) {
        const apiName = apiId.match(/managedApis\/([^/]+)$/)?.[1];
        if (apiName) {
          names.add(apiName);
        }
      }
    }

    // Also extract from per-action display refs so connection-less actions
    // (e.g. just-added managed-API actions) still trigger their connector fetch.
    for (const op of operationDisplayRefs) {
      if (op.apiId) {
        const apiName = extractApiName(op.apiId) ?? op.apiId.split('/').pop();
        if (apiName) names.add(apiName);
      }
    }

    return Array.from(names);
  }, [enrichedRefs, connectionReferences, operationDisplayRefs]);

  // Fetch service provider manifests
  const { data: serviceProviderManifests, isLoading: isLoadingServiceProviders } = useQuery({
    queryKey: ['serviceProviderManifests', selectedProject?.toLowerCase(), selectedAppName?.toLowerCase()],
    queryFn: async () => {
      if (!selectedAppName || !selectedProject) return new Map<string, ConnectorManifest>();

      try {
        return await fetchServiceProviderManifests({
          projectName: selectedProject ?? '',
          appName: selectedAppName,
          getToken: getOttoToken,
        });
      } catch (error) {
        console.warn('Failed to fetch service provider manifests (this is optional):', error);
        return new Map<string, ConnectorManifest>();
      }
    },
    enabled: !!selectedAppName && !!selectedProject && serviceProviderIds.length > 0,
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    retry: false, // Don't retry on 404
  });

  // Fetch managed API manifests via data-plane (one query per unique API)
  const managedApiQueries = useQueries({
    queries: managedApiNames.map((apiName) => ({
      queryKey: ['managedApiManifest', selectedProject?.toLowerCase(), apiName],
      queryFn: async () => {
        if (!apiName) return null;

        try {
          return await fetchConnectorManifest({
            projectName: selectedProject ?? '',
            connectorName: apiName,
            getToken: getOttoToken,
          });
        } catch (error) {
          console.warn(`Failed to fetch manifest for managed API ${apiName}:`, error);
          return null;
        }
      },
      enabled: !!selectedProject,
      staleTime: 8 * 60 * 60 * 1000, // 8 hours
    })),
  });

  // Build managed API manifests map
  const managedApiManifests = useMemo(() => {
    const map = new Map<string, ConnectorManifest>();
    managedApiQueries.forEach((query, index) => {
      if (query.data) {
        const apiName = managedApiNames[index];
        if (apiName) {
          map.set(apiName, query.data);
        }
      }
    });
    return map;
  }, [managedApiQueries, managedApiNames]);

  const isLoadingManagedApis = managedApiQueries.some((q) => q.isLoading);

  // Build connector info map
  const connectorMap = useMemo(() => {
    const map = new Map<string, NodeConnectorInfo>();

    for (const ref of enrichedRefs) {
      // Try service provider manifest
      if (ref.serviceProviderId && serviceProviderManifests) {
        const manifest = serviceProviderManifests.get(ref.serviceProviderId);
        if (manifest) {
          map.set(ref.connectionName, manifestToInfo(manifest, 'serviceProvider'));
          continue;
        }
      }

      // Try managed API - first check if we have manifest by apiId
      if (ref.apiId) {
        const apiName = extractApiName(ref.apiId);
        if (apiName) {
          const manifest = managedApiManifests.get(apiName);
          if (manifest) {
            const info = manifestToInfo(manifest, 'managedApi');
            map.set(ref.connectionName, info);
            // Also key by API name so other connections with the same connector can find it
            if (!map.has(apiName)) {
              map.set(apiName, info);
            }
            continue;
          }
        }
      }

      // Try managed API - also check if connection name itself is a managed API
      // (for tool inner actions where apiId might not be in connections.json)
      const manifestByConnectionName = managedApiManifests.get(ref.connectionName);
      if (manifestByConnectionName) {
        map.set(ref.connectionName, manifestToInfo(manifestByConnectionName, 'managedApi'));
        continue;
      }

      // Try agent connection - use model info from action inputs
      if (ref.agentModelType) {
        const displayName = ref.agentModelName
          ? `${ref.agentModelType} (${ref.agentModelName})`
          : ref.agentModelType;
        map.set(ref.connectionName, {
          displayName,
          connectorType: 'agent',
        });
        continue;
      }

      // Check if this is an agent connection in connections.json (fallback)
      if (connections?.agentConnections?.[ref.connectionName]) {
        map.set(ref.connectionName, {
          displayName: 'AI Agent',
          connectorType: 'agent',
        });
      }
    }

    // Also map connection store reference keys to their connector info.
    // This ensures connections that exist in the store but aren't yet used
    // in any workflow action still have icons available.
    for (const [key, ref] of Object.entries(connectionReferences)) {
      if (map.has(key)) continue; // Already mapped from workflow definition

      const apiName = extractApiName(ref.api?.id ?? '');
      if (!apiName) continue;
      const manifest = managedApiManifests.get(apiName);
      if (!manifest) continue;

      const info = manifestToInfo(manifest, 'managedApi');
      map.set(key, info);
      // Also key by API name so data-plane connector lookups work
      if (!map.has(apiName)) {
        map.set(apiName, info);
      }
    }

    // Per-action display fallback: every action/trigger that has a connectorId
    // (Service Provider id or managed-API id) gets keyed by its action name so
    // floating nodes can resolve display info even before a connection exists
    // (and after reload, when display fields are no longer carried in metadata).
    //
    // Action names are user-editable, so we namespace them with `action:` to
    // avoid colliding with managed-API names or connection reference keys
    // (e.g., a Service Bus action renamed to `office365` would otherwise
    // resolve to the Office 365 manifest).
    for (const op of operationDisplayRefs) {
      const actionKey = actionNameKey(op.actionName);
      if (map.has(actionKey)) continue;

      if (op.serviceProviderId && serviceProviderManifests) {
        const manifest = serviceProviderManifests.get(op.serviceProviderId);
        if (manifest) {
          map.set(actionKey, manifestToInfo(manifest, 'serviceProvider'));
          continue;
        }
      }

      if (op.apiId) {
        const apiName = extractApiName(op.apiId) ?? op.apiId.split('/').pop();
        const manifest = apiName ? managedApiManifests.get(apiName) : undefined;
        if (manifest) {
          map.set(actionKey, manifestToInfo(manifest, 'managedApi'));
          continue;
        }
      }

      // Built-in literal-type fallback (Batch / ChunkText / XmlCompose / …).
      // Skip types covered by a curated static manifest so the canonical icon
      // wins over whatever the runtime catalog reports.
      if (op.type && !getBuiltInManifest(op.type, op.kind)) {
        const builtInOp = builtInOpsByType.get(op.type.toLowerCase());
        if (builtInOp) {
          map.set(actionKey, builtInOpToInfo(builtInOp, op.type));
        }
      }
    }

    return map;
  }, [enrichedRefs, serviceProviderManifests, managedApiManifests, connections, connectionReferences, operationDisplayRefs, builtInOpsByType]);

  const getConnectorInfo = useCallback(
    (connectionName: string): NodeConnectorInfo | undefined => connectorMap.get(connectionName),
    [connectorMap],
  );
  const isLoading = isLoadingServiceProviders || isLoadingManagedApis;

  return useMemo(
    () => ({ connectorMap, getConnectorInfo, isLoading }),
    [connectorMap, getConnectorInfo, isLoading],
  );
};
